import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    useMediaQuery,
    useTheme
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroller";

const ModernDataTable = ({
                             // Data props
                             data = [],
                             columns = [],
                             totalCount = 0,

                             // Loading & pagination
                             isLoading = false,
                             hasMore = false,
                             onLoadMore = null,

                             // Styling props
                             containerSx = {},
                             tableSx = {},

                             // Row props
                             onRowClick = null,
                             renderRow = null,

                             // Empty state
                             emptyStateTitle = "אין תוצאות",
                             emptyStateSubtitle = "נסה לשנות את הפילטרים או החיפוש",

                             // Infinite scroll props
                             useInfiniteScroll = false,

                             // Custom components
                             children = null
                         }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const defaultContainerSx = {
        backgroundColor: "#FCFCFD",
        border: "1px solid #E2E8F0",
        borderRadius: "8px",
        direction: "rtl",
        overflow: "auto",
        overflowY: "hidden",
        ...containerSx
    };

    // Mobile card layout - just render children in a simple container
    const renderMobileContent = () => (
        <Box sx={{ padding: "12px" }}>
            {/* Mobile header with count */}
            {totalCount > 0 && (
                <Typography
                    sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#64748B",
                        marginBottom: "12px",
                        textAlign: "right"
                    }}
                >
                    לקוחות ({totalCount})
                </Typography>
            )}
            {children}
        </Box>
    );

    // Desktop table layout
    const renderTableContent = () => (
        <TableContainer>
            <Table sx={tableSx}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                        {columns.map((column, index) => (
                            <TableCell
                                key={index}
                                align={column.align || "center"}
                                width={column.width}
                                sx={{
                                    borderBottom: "1px solid #E2E8F0",
                                    padding: "12px 16px",
                                    color: "#64748B",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    ...column.headerSx
                                }}
                            >
                                {column.header}
                                {column.showCount && totalCount > 0 && ` (${totalCount})`}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {children || (
                        data.map((item, index) =>
                            renderRow ? renderRow(item, index) : (
                                <TableRow
                                    key={item.id || index}
                                    onClick={onRowClick ? () => onRowClick(item) : undefined}
                                    sx={{
                                        cursor: onRowClick ? "pointer" : "default",
                                        "&:hover": onRowClick ? {
                                            backgroundColor: "#F8FAFC"
                                        } : {},
                                        borderBottom: "1px solid #E2E8F0"
                                    }}
                                >
                                    {columns.map((column, colIndex) => (
                                        <TableCell
                                            key={colIndex}
                                            align={column.align || "center"}
                                            sx={{
                                                padding: "12px 16px",
                                                borderBottom: "none",
                                                ...column.cellSx
                                            }}
                                        >
                                            {column.render ? column.render(item, index) : item[column.field]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )
                        )
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderContent = () => isMobile ? renderMobileContent() : renderTableContent();

    const renderEmptyState = () => (
        <Box sx={{
            margin: { md: "40px auto", xs: "40px 20px" },
            textAlign: "center",
            padding: "40px 20px"
        }}>
            <Typography
                sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#64748B",
                    textAlign: "center",
                }}
            >
                {emptyStateTitle}
            </Typography>
            {emptyStateSubtitle && (
                <Typography
                    sx={{
                        fontSize: "14px",
                        color: "#94A3B8",
                        textAlign: "center",
                        marginTop: "8px"
                    }}
                >
                    {emptyStateSubtitle}
                </Typography>
            )}
        </Box>
    );

    const renderLoader = () => (
        <Box
            key="loader"
            sx={{
                display: "flex",
                justifyContent: "center",
                margin: "16px",
                padding: "20px"
            }}
        >
            <CircularProgress
                size={40}
                sx={{ color: "#10B981" }}
            />
        </Box>
    );

    // An EMPTY array of children (e.g. `{items.map(...)}` when items is []) is still truthy,
    // so a plain `|| children` forced hasData true and rendered a headers-only table instead
    // of the empty state. Treat empty-array children as "no content" so consumers that pass a
    // mapped list (MiniCoupons, MiniTemplates, …) correctly fall through to renderEmptyState().
    const hasChildrenContent = Array.isArray(children) ? children.length > 0 : Boolean(children);
    const hasData = data.length > 0 || isLoading || hasChildrenContent;

    if (useInfiniteScroll) {
        return (
            <Paper sx={defaultContainerSx} elevation={0}>
                <InfiniteScroll
                    initialLoad={true}
                    hasMore={hasMore}
                    loadMore={onLoadMore}
                    loader={renderLoader()}
                >
                    <Box key="content">
                        {hasData ? renderContent() : renderEmptyState()}
                    </Box>
                </InfiniteScroll>
            </Paper>
        );
    }

    return (
        <Paper sx={defaultContainerSx} elevation={0}>
            {hasData ? renderContent() : renderEmptyState()}
        </Paper>
    );
};

export default ModernDataTable;